import { Dictionary } from 'lodash'
import React from 'react'
import { connect } from 'react-redux'
import * as actions from '../actions/customerDetailsActions'
import * as orderActions from '../actions/orderDetailsActions'
import { Customer, GlobalConfiguration, OrderListItemModel, PermissionsView, PhotoOrganizerInactivityWarning } from '../api'
import ActionLink from '../components/generic/ActionLink'
import Grid, { GridColumn } from '../components/generic/Grid'
import OrderList from '../components/lists/OrderList'
import { CommonProps } from '../models'
import { AppStore, CustomerDetailsStore, CustomerDetailsViewStore } from '../reducers/states'
import { selectOnClick, toDateTimeDetailed, toDateTimeNew } from '../utils'
import MigrationInfo from '../components/lists/MigrationInfo'
import { EmailDelivery, PastState, PhotoDeletionAccountCycle, StateMetadata, EmailConfiguration } from '../api/photoDeletionAccountCycle'
import CollapsableContainer from '../components/generic/CollapsableContainer'
import * as moment from 'moment'

interface Props extends CommonProps, CustomerDetailsStore, CustomerDetailsViewStore {
  configuration: GlobalConfiguration
  orders: Dictionary<OrderListItemModel>
  permissions?: PermissionsView
}

class CustomerDetailsView extends React.Component<Props> {
  private foldedCustomers: Array<boolean>

  constructor(props: Props) {
    super(props)

    this.loadMore = this.loadMore.bind(this)
    this.toggleAllDetails = this.toggleAllDetails.bind(this)
    this.toggleOrderDetails = this.toggleOrderDetails.bind(this)
    this.toggleCustomerMigrationDetails = this.toggleCustomerMigrationDetails.bind(this)
    this.requestOrderDetails = this.requestOrderDetails.bind(this)

    this.foldedCustomers = new Array<boolean>(true)
  }

  componentDidMount() {
    if (!this.props.data || this.props.data.email.toLowerCase() !== this.props.match.params.email.toLowerCase()) {
      this.props.dispatch(actions.requestDetails(this.props.match.params.email))
    }
  }

  toggleAllDetails() {
    this.props.dispatch(actions.toggleAllDetails())
  }

  toggleOrderDetails(index: number) {
    this.props.dispatch(actions.toggleOrderDetails(index))
  }

  toggleCustomerMigrationDetails() {
    this.foldedCustomers[0] = !this.foldedCustomers[0]
    this.forceUpdate()
  }

  requestOrderDetails(id: string) {
    this.props.dispatch(orderActions.requestOrderHistoryDetails(id))
  }

  loadMore() {
    if (this.props.prevAlbelliResponse.hasMore) {
      this.props.dispatch(actions.requestAlbelliOrders())
    }
  }

  renderLinks() {
    return (
      <React.Fragment>
        <ActionLink
          text="< back"
          className="mr-4"
          onClick={() => {
            this.props.history.goBack()
          }}
        />
      </React.Fragment>
    )
  }

  renderDetails(data: Customer) {
    const columns: GridColumn<Customer>[] = [
      { title: 'Salutation', value: (item) => item.salutation },
      { title: 'Name', value: (item) => item.name },
      { title: 'Created at', value: (item) => toDateTimeNew(item.createdAt) },
      {
        title: '',
        value: (_) => (
          <React.Fragment>
            {data.migrationDetails && (
              <ActionLink
                text={this.foldedCustomers[0] == true ? 'Show migration info' : 'Hide migration info'}
                onClick={this.toggleCustomerMigrationDetails}
              />
            )}
          </React.Fragment>
        ),
      },
    ]

    return (
      <React.Fragment>
        <div className="bo-container mt-3">
          <h2 className="m-0" onClick={selectOnClick}>
            {data.email}
          </h2>
        </div>
        <Grid
          columns={columns}
          data={[data]}
          className="mt-3 pl-3"
          disableCopyOnClick={true}
          extraDetails={data.migrationDetails ? this.renderExtraDetails : false}
          showFolderToggleIcon={false}
          foldedRows={this.foldedCustomers}
        />
      </React.Fragment>
    )
  }

  renderExtraDetails(customer: Customer, _: GridColumn<Customer>[]) {
    return (
      <React.Fragment>
        {customer.migrationDetails && <MigrationInfo details={customer.migrationDetails}></MigrationInfo>}
      </React.Fragment>
    )
  }

  renderOrders() {
    const hasMore = this.props.prevAlbelliResponse.hasMore
    return (
      <React.Fragment>
        <div className="d-flex justify-content-between mt-3">
          <h3>Orders</h3>
          <ActionLink
            text={this.props.historyFolded ? 'Show all product details' : 'Close all product details'}
            onClick={this.toggleAllDetails}
          />
        </div>
        <OrderList
          albelliOrders={this.props.prevAlbelliResponse.items}
          enableExtraDetails={true}
          foldAll={this.props.historyFolded}
          foldedRows={this.props.foldedOrders}
          onToggleRow={this.toggleOrderDetails}
          orders={this.props.orders}
          onOrderVisible={this.requestOrderDetails}
          showCustomerName={false}
          showPrice={true}
        />
        {hasMore && (
          <button className="mt-4 wide-button" onClick={this.loadMore}>
            Load more orders
          </button>
        )}
      </React.Fragment>
    )
  }

  getEmailDeliveryStatus = (emailDeliveryStatus: string) => {
    const emailDeliveryStatusHumanized: Map<string, string> = new Map([
      ['SUCCESS', 'delivered to inbox'],
      ['SOFT_BOUNCE', 'temporarily undeliverable (soft bounce)'],
      ['HARD_BOUNCE', 'permanently undeliverable (hard bounce)'],
      ['FAILURE', 'delivery failed'],
    ])

    return (
      emailDeliveryStatusHumanized.get(emailDeliveryStatus) ??
      'COULD NOT MAP EMAILDELIVERYSTATUS, ERROR. EmailDeliveryStatus: ' + emailDeliveryStatus
    )
  }

  renderPhotoDeletionAccountCycle(
    data: PhotoDeletionAccountCycle,
    folded: boolean = true,
    isPreviousPhotoDeletionAccountCycle = false
  ) {
    const statusColumns: GridColumn[] = [
      {
        title: 'Status',
        value: (item: PastState) => this.getStatus(item.state, data),
        tdClass: 'text-overflow-normal',
      },
      {
        title: 'Email address',
        value: (item: PastState) => item.metadata?.email,
      },
      {
        title: 'Happened at',
        value: (item: PastState) => {
          // Check if the current row's state is an email delivery status
          if (item.state.includes('EmailDelivered') || item.state === 'NeverGotEmailDeliveryStatus') {
            return ''; // Return an empty string for these rows
          }
          // Otherwise, return the formatted date
          return toDateTimeNew(item.createdAt);
        },
      },
    ]

    const addFractionalMonths = (d: moment.Moment, months: number) => {
      const whole = Math.trunc(months);
      const frac  = months - whole;
      const res = d.clone().add(whole, "months");
      if (frac) {
        const days = res.daysInMonth();
        res.add(Math.round(frac * days), "days");
      }
      return res;
    };

    let projectedDeletionDateColumns: GridColumn[] = [];
    const projectedDeletionDates: { email: string; date: string }[] = [];

    let cur = moment.utc(data.createdAt);
    for (let i = 0; i < data.communicationConfiguration.emails.length; i++) {
      projectedDeletionDates.push({
        email: `Email ${i + 1}`,
        date: cur.format("DD/MM/YYYY"),
      });

      const waitStr = data.communicationConfiguration.emails[i]?.waitTimeAfterActionInMonths;
      const wait = waitStr != null ? Number.parseFloat(String(waitStr)) : 0;
      if (!Number.isNaN(wait) && i < data.communicationConfiguration.emails.length - 1) {
        cur = addFractionalMonths(cur, wait);
      }
    }

    projectedDeletionDateColumns.push(
      { title: "Email", value: (e: any) => e.email },
      { title: "Date",  value: (e: any) => e.date }
    );

    const deletedAt = (() => {
      if (data.audit.currentState === 'PhotosDeleted') return data.audit.currentStateCreatedAt
      const s = data.audit.pastStates.filter(p => p.state === 'PhotosDeleted')
      if (s.length > 1) return undefined // inconsistent history; avoid showing a wrong date
      return s[0]?.createdAt
    })()

    return (
      <>
        {data.deletionHasBeenDelayed && !data.projectActivityDeletionDelayed && (
          <div className="bo-container mt-3 warning-color">
            <h2>Delayed Deletion</h2>
            <p>
              Deletion has been delayed for this account.
              It will be deleted on: <b>{toDateTimeNew(data.delayedDeletionDate)}</b>
            </p>
          </div>
        )}

        {data.projectActivityDeletionDelayed && (
          <div className="bo-container mt-3 warning-color">
            <h2>Delayed Deletion</h2>
            <p>
              Deletion has been delayed for this account due to a recent project activity.
              It will be deleted on: <b>{toDateTimeNew(data.delayedDeletionDate)}</b>
            </p>
          </div>
        )}

        {deletedAt && (
          <div className="bo-container mt-3 warning-color">
            <h2>Photos deleted</h2>
            <p>
              All photos for this account were deleted on <b>{toDateTimeNew(deletedAt)}</b>.
            </p>
          </div>
        )}

        {data.audit.pastStates.length > 0 && (
          <div className={isPreviousPhotoDeletionAccountCycle ? 'indent-padding-left' : ''}>
            {isPreviousPhotoDeletionAccountCycle && (
              <>
                <br />
                <p>
                  The status of the account in this deletion process was:{' '}
                  <b>
                    {this.getStatus(data.audit.currentState, data)}
                  </b>
                  . This happened at <b>{toDateTimeNew(data.audit.currentStateCreatedAt)}</b>
                  <br />
                  This deletion process is currently <b>{data.isCycleFullyFinished ? 'finished' : 'in progress'}</b>.
                </p>
                <br />
                <h3>Process timeline</h3>
              </>
            )}
            <CollapsableContainer title="Historic statuses" folded={folded}>
              <Grid columns={statusColumns} data={this.getHistoricStatuses(data)} disableCopyOnClick={true} />
            </CollapsableContainer>
          </div>
        )}
        <CollapsableContainer title="Email Dispatch Dates" folded={true}>
          <Grid columns={projectedDeletionDateColumns} data={projectedDeletionDates} disableCopyOnClick={true} />
        </CollapsableContainer>
        {this.props.permissions?.isExpert && (
          <CollapsableContainer title={'Developer show JSON of AccountCycle making up this view'} folded={true}>
            <pre>
              <code>{JSON.stringify(data, null, 4)}</code>
            </pre>
          </CollapsableContainer>
        )}
      </>
    )
  }

  getHistoricStatuses = (data: PhotoDeletionAccountCycle) => {
    const states = data.audit.pastStates.slice();
    states.push({
      state: data.audit.currentState,
      createdAt: data.audit.currentStateCreatedAt,
      lastUpdatedAt: data.audit.currentStateCreatedAt,
      metadata: data.audit.currentStateMetadata as StateMetadata,
    } as PastState);
    if (data.pushNotificationDeliveries && data.pushNotificationDeliveries.length > 0) {
       let message = 'Push notification sent';
       if (data.pushNotificationDeliveries[0].wasPushNotificationOpened) {
          message += ' (opened)';
       }
       states.push({
          state: message,
          createdAt: data.pushNotificationDeliveries[0].createdAt,
          lastUpdatedAt: data.pushNotificationDeliveries[0].createdAt,
          metadata: {} as StateMetadata,
        } as PastState);
    }
    return states.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }

  getMatchedEmailDeliveryStatus = (deliveries: EmailDelivery[], state: string) => {
    const stateNumber = parseInt(state.split('-')[0], 10);
    if (isNaN(stateNumber)) {
      return undefined;
    }

    const sorted = deliveries.slice().sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    return sorted[stateNumber - 1];
  }

  getStatus = (state: string, data: PhotoDeletionAccountCycle, colorful: boolean = false) => {
    const statusHumanized: Map<string, string> = new Map([
      ['Initial', 'Start of process'],
      ['1-EmailDispatched', 'Trying to send first email'],
      ['2-EmailDispatched', 'Trying to send second email'],
      ['3-EmailDispatched', 'Trying to send third email'],
      ['4-EmailDispatched', 'Trying to send fourth email'],
      ['5-EmailDispatched', 'Trying to send fifth email'],
      ['1-EmailDelivered', 'First email was '],
      ['2-EmailDelivered', 'Second email was '],
      ['3-EmailDelivered', 'Third email was '],
      ['4-EmailDelivered', 'Fourth email was '],
      ['5-EmailDelivered', 'Fifth email was '],

      ['AccountCouldNotBeNotified', 'Could not notify account in any way'],
      ['AccountDeletedExternally', 'Account was deleted by an external service'],
      ['AccountEmailIsInvalid', 'Account email is invalid'],
      [
        'AccountHasPlacedOrder',
        'While checking to continue the process, the account has placed an order since last time the process ran',
      ],
      ['OrderProductionReadyHappened', 'Account placed an order'],
      ['QueuedForDeletion', 'Account is queued for photo deletion'],
      ['PhotosDeleted', 'Account photos deleted'],
      ['NeverGotEmailDeliveryStatus', 'Email was never delivered'],
      ['NotReadyToSendNextEmail', 'Process was triggered before next email could be sent'],
      ['AccountIsParked', 'Account is parked for now'],
      ['AccountCycleFailed', 'The process was exited and will be started again from the start later on'],
      ['DelayedDueToRecentProjectActivity', 'Deletion delayed due to recent project activity'],
      ['AccountIsWhitelisted', 'Account is Whitelisted, therefore the photo deletion process will not run'],

      // Added only for use now as a bit of a hack, re-design we should remove the hackiness!
      ['Push notification sent', 'Push notification sent'],
      ['Push notification sent (opened)', 'Push notification sent (opened)'],
    ])


    var stateHumanized = statusHumanized.get(state) ?? 'COULD NOT MAP STATUS, ERROR. State value:' + state;

    if (state.includes('EmailDelivered')) {
      const deliveries = data.emailDeliveries;
      if (deliveries.length == 0) {
        return 'COULD NOT MAP DELIVERY STATUS, ERROR'
      }

      const deliveryStatus = this.getMatchedEmailDeliveryStatus(deliveries, state)?.deliveryStatus;
      if (deliveryStatus == undefined) {
        return 'COULD NOT MAP DELIVERY STATUS, ERROR'
      }

      const formattedStatus = stateHumanized + this.getEmailDeliveryStatus(deliveryStatus);
      if (!colorful) {
        return formattedStatus
      }
      else {
        switch (deliveryStatus) {
          case 'SUCCESS':
            return <span className="success-color">{formattedStatus}</span>
          case 'SOFT_BOUNCE':
            return <span className="warning-color">{formattedStatus}</span>

          case 'HARD_BOUNCE':
          case 'FAILURE':
            return <span className="danger-color">{formattedStatus}</span>
        }
      }
      return
    }

    if (data.isCycleFullyFinished && colorful && data.audit.currentState == "OrderProductionReadyHappened") {
      return <span className="success-color">{stateHumanized}</span>
    }

    return stateHumanized
  }
renderPhotoDeletionData(data: PhotoDeletionAccountCycle[]) {
  if (data.length === 0) {
    return
  }

  const deletedAtColumn: GridColumn = {
    title: 'Deleted at',
    value: (item: PhotoDeletionAccountCycle) => {
      if (item.audit.currentState === 'PhotosDeleted') {
        return toDateTimeNew(item.audit.currentStateCreatedAt)
      }

      const filteredStates = item.audit.pastStates.filter((p) => p.state === 'PhotosDeleted')
      if (filteredStates.length > 1) {
        return 'ERROR, cannot parse, invalid state, photosdeleted multiple times'
      }
      if (filteredStates.length === 1) {
        return toDateTimeNew(filteredStates[0].createdAt)
      }

      return undefined
    },
  }

  let accountInformationColumns: GridColumn[] = [
    {
      title: 'Deletion date',
      value: (item: PhotoDeletionAccountCycle) =>
        item.deletionHasBeenDelayed
          ? toDateTimeNew(item.delayedDeletionDate)
          : toDateTimeNew(item.projectedDeletionDate),
    },
    deletedAtColumn,
    {
      title: 'Last album count (estimate)',
      value: (item: PhotoDeletionAccountCycle) => `${item.lastAlbumCount} albums`,
    },
    {
      title: 'Last photo count (estimate)',
      value: (item: PhotoDeletionAccountCycle) =>
        `${Number(item.lastPhotoCount) >= 2000 ? '2000 or more' : item.lastPhotoCount} photos`,
    },
    {
      title: 'Order before date',
      value: (item: PhotoDeletionAccountCycle) =>
        item.deletionHasBeenDelayed
          ? toDateTimeNew(item.delayedDeletionDate)
          : toDateTimeNew(item.projectedDeletionDate),
    },
    { title: 'Channel', value: (item: PhotoDeletionAccountCycle) => item.channel },
    { title: 'AccountId', value: (item: PhotoDeletionAccountCycle) => item.accountId },
  ]

  // hide “Deleted at” column if all rows have it empty
  const hasDeletedAt = data.some(
    (d, i) => deletedAtColumn.value && !!deletedAtColumn.value(d, i)
  )

  if (!hasDeletedAt) {
    accountInformationColumns = accountInformationColumns.filter((c) => c.title !== 'Deleted at')
  }

  const activeAccountCycle = data[0]

  return (
    <div className="bo-container mt-3">
      <h2>Photo Deletion Account Information</h2>

      <p>
        The current status of the account in the deletion process is:{' '}
        <b>{this.getStatus(activeAccountCycle.audit.currentState, activeAccountCycle, true)}</b>. This happened at{' '}
        <b>{toDateTimeNew(activeAccountCycle.audit.currentStateCreatedAt)}</b>
        <br />
        The {activeAccountCycle.audit.currentState === 'OrderProductionReadyHappened' ? 'reactivation' : 'deletion'} process is currently{' '}
        <b>{activeAccountCycle.isCycleFullyFinished ? 'finished' : 'in progress'}</b>.
      </p>

      <h3>Account information</h3>
      <Grid columns={accountInformationColumns} data={[activeAccountCycle]} disableCopyOnClick={false} />
      <br />
      <h3>Process timeline</h3>
      {this.renderPhotoDeletionAccountCycle(activeAccountCycle, false)}
      <br />

      {data.length > 1 && <hr />}
      {data.length > 1 &&
        data.slice(1).map((accountCycle) => (
          <CollapsableContainer
            key={accountCycle.id}
            titleComponent={
              <span key={'titleComponent'}>
                Previous process that <b>{accountCycle.isCycleFullyFinished ? 'finished' : 'stopped'}</b> with{' '}
                <b>{this.getStatus(accountCycle.audit.currentState, accountCycle)}</b> on{' '}
                <b>{toDateTimeNew(accountCycle.audit.currentStateCreatedAt)}</b>
              </span>
            }
            folded={true}>
            {this.renderPhotoDeletionAccountCycle(accountCycle, false, true)}
          </CollapsableContainer>
        ))}
    </div>
  )
}

  //Users with inactive photoorganiser accounts gets deleted, and CC needs to
  //have some information on warning emails sent, deletion date etc.
  renderPhotoorganiserDeletionData(data: PhotoOrganizerInactivityWarning[]) {
    const notificationTypes: Map<string, string> = new Map([
      ['PhotoOrganizerFirstInactivityWarning', 'First reminder'],
      ['PhotoOrganizerSecondInactivityWarning', 'Second reminder'],
      ['PhotoOrganizerFinalInactivityWarning', 'Final deletion warning'],
      ['PhotoOrganizerOrderPerformed', 'Order placed, deletion halted'],
      ['PhotoOrganizerAccountDeleted', 'Account deleted'],
      ['PhotoOrganizerSmsSent', 'Sent SMS'],
      ['PhotoOrganizerUserHasNoContact', 'No Contact'],
      ['PhotoOrganizerEmailBounced', 'Email bounced'],
      ['PhotoOrganizerEmailInvalid', 'Email invalid'],
      ['PhotoOrganizerSmsFailure', 'SMS failed'],
      ['PhotoOrganizerAccountCouldNotBeNotified', 'Account could not be notified'],
    ])

    const getStateMessage = (item: any) => {
      // If the account has no photos, we instantly delete the account without bothering with emails or texts
      // Thus we should show a different copy to indicate this is intended behaviour and not just the copy 'Account was deleted'
      if (item.state == 'Deleted' && !item.nrPicturesStored && !!data && data.length == 1) {
        return 'This account was deleted instantly as it did not have any photos. For this reason there are no emails or texts sent to the customer.'
      }

      return item.stateMessage
    }

    const columns: GridColumn[] = [
      { title: 'Channel', value: (item) => item.channel },
      { title: 'Type', value: (item) => notificationTypes.get(item.emailType) },
      { title: 'Sent to', value: (item) => item.email },
      { title: 'Sent at', value: (item) => toDateTimeDetailed(item.emailSentAtUtc) },
      { title: 'Order before date', value: (item) => toDateTimeDetailed(item.orderBeforeDateTime) },
      { title: 'Album count', value: (item) => item.amountAlbums },
      { title: 'Photo count', value: (item) => item.nrPicturesStored },
      { title: '', value: (item) => getStateMessage(item), tdClass: 'wrap-normal' },
    ]

    return (
      <div className="bo-container mt-3">
        <h2>Photo Organiser Delete Account Warnings</h2>
        <Grid columns={columns} data={data} className="mt-3" disableCopyOnClick={true} />
        <div style={{ margin: '10px' }}>
          <span>ℹ️</span>
          <span style={{ fontStyle: 'italic' }}>
            The customer reactivation program for NO/SE has been paused since April 22nd. It will resume as part of the Storio
            Reactivation program planned August 2025. The program will be reset and all accounts will again start from 1st email
            notification.
          </span>
        </div>
      </div>
    )
  }

  render() {
    return (
      <React.Fragment>
        {this.renderLinks()}
        {this.props.data && this.renderDetails(this.props.data)}
        {this.props.data?.photoDeletionAccountCycles && this.renderPhotoDeletionData(this.props.data.photoDeletionAccountCycles)}
        {this.props.data?.poInactivityWarnings && this.renderPhotoorganiserDeletionData(this.props.data.poInactivityWarnings)}
        {this.renderOrders()}
      </React.Fragment>
    )
  }
}

function mapStateToProps(state: AppStore) {
  return {
    ...state.customerDetails,
    ...state.customerDetailsView,
    orders: state.orderDetails.orders,
    configuration: state.global.configuration,
    ...state.router,
    ...state.global?.configuration,
  } as Props
}

export default connect(mapStateToProps)(CustomerDetailsView)
